import { Slottable } from "@radix-ui/react-slot";
import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import React from "react";
import { useFormState } from "../../form/Form/Form";
import { Kbd } from "../Kbd/Kbd";
import Pressable, { type PressableProps } from "../Pressable/Pressable";
import Tooltip from "../Tooltip/Tooltip";
import styles from "./Button.module.scss";
import "../../../styles/focus-ring.scss";
import { withRefProp } from "../../../utils/withRefProp";
import { PopoverProps } from "../Popover/Popover";

export type ButtonVariant = "contained" | "outline" | "ghost" | "link";
export type ButtonIntent = "primary" | "secondary" | "destructive" | "warning" | "sidebar" | "dark" | "light";
/** "xs" is a fixed 24px high, e.g. for actions inside a 36px input. */
export type ButtonSize = "xs" | "sm" | "md" | "default" | "lg";

export interface ButtonProps extends PressableProps {
  /** Accessible label used for icon buttons and tooltips. */
  label: string;
  /** Visual style of the button. @default "contained" (or "ghost" when `icon` is true) */
  variant?: ButtonVariant;
  /** Color intent of the button. @default "secondary" */
  intent?: ButtonIntent;
  /** Shows a loading spinner and disables interaction. */
  loading?: boolean;
  /** Size of the button. @default "md" */
  size?: ButtonSize;
  /** When true, enforces a square aspect ratio and hides padding, sizing the button by `size`. */
  icon?: boolean;
  /** When true, shows the button as selected using the primary subtle treatment. */
  selected?: boolean;
  /** Tooltip content. Pass `false` to explicitly suppress the tooltip. */
  tooltip?: React.ReactNode | false;
  /** When true, the tooltip will still show even when the button is disabled. */
  allowDisabledTooltips?: boolean;
  /** Placement of the tooltip relative to the button. */
  placement?: PopoverProps["placement"];
  /** Keyboard shortcut(s) displayed inside the tooltip. */
  hotkeys?: string | string[];
  ref?: React.Ref<HTMLButtonElement>;
}

const DefaultButton = withRefProp((props: ButtonProps) => {
  const {
    label,
    className,
    variant,
    intent = "secondary",
    children,
    loading = false,
    size = "md",
    icon = false,
    selected = false,
    tooltip,
    allowDisabledTooltips = false,
    hotkeys,
    disabled,
    ref,
    ...otherProps
  } = props;
  const formState = useFormState();
  const isSubmitButton = props.type === "submit";
  const isSubmittingFromForm = Boolean(isSubmitButton && formState?.isSubmitting);
  //const isInvalidFromForm = Boolean(isSubmitButton && formState && !formState.isValid);

  const resolvedVariant = variant ?? (icon ? "ghost" : "contained");
  const resolvedIntent = intent;

  const resolvedSize = size === "md" ? "default" : size;
  const resolvedChildren = children == null ? label : children;

  const isLoading = loading || isSubmittingFromForm;
  const isDisabled = disabled || isLoading /*|| isInvalidFromForm*/;

  const hotkeyList = Array.isArray(hotkeys) ? hotkeys : hotkeys ? [hotkeys] : [];
  const resolvedTooltip = icon ? (tooltip ?? label) : tooltip;
  const hasTooltip = resolvedTooltip !== false && (resolvedTooltip !== undefined || hotkeyList.length > 0);

  const button = (
    <Pressable
      ref={ref}
      className={clsx(styles.button, className)}
      {...otherProps}
      data-deci-button="true"
      data-variant={resolvedVariant}
      data-intent={resolvedIntent}
      data-size={resolvedSize}
      data-loading={isLoading}
      data-icon={icon ? "true" : undefined}
      data-selected={selected ? "true" : undefined}
      aria-pressed={selected ? true : otherProps["aria-pressed"]}
      data-focus-ring
      aria-label={otherProps["aria-label"] ?? label}
      disabled={isDisabled}
      style={isDisabled && allowDisabledTooltips ? { pointerEvents: "auto", ...otherProps.style } : otherProps.style}
    >
      <Pressable.Ripple />
      {isLoading && (
        <span className={styles.loadingOverlay}>
          <span className={styles.spinner} />
        </span>
      )}
      <Slottable>{resolvedChildren}</Slottable>
    </Pressable>
  );

  if (!hasTooltip) {
    return button;
  }

  return (
    <Tooltip placement={props.placement} disabled={isDisabled && !allowDisabledTooltips}>
      <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
      <Tooltip.Content>
        <span className={styles.tooltipBody}>
          {resolvedTooltip !== undefined && <span>{resolvedTooltip}</span>}
          {hotkeyList.length > 0 && (
            <span className={styles.tooltipHotkeys}>
              <Kbd
                variant="separate"
                color="light"
                keys={hotkeyList}
              />
            </span>
          )}
        </span>
      </Tooltip.Content>
    </Tooltip>
  );
});

const Button = createPluggable<ButtonProps>("deci.button", DefaultButton);

export default Button;
